import { HighlightDroppable } from "@/components/forms/components/HighlightDroppable.tsx";
import {
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { CustomDraggableItem } from "@/pages/settings/custom-fields/components/CustomDraggableItem.tsx";
import {
  CustomDraggableNode,
  CustomDraggableNodeType,
} from "@/pages/settings/custom-fields/custom-fields.ts";
import { useTableFields, useTableFieldsSort } from "@/queries/use-custom-fields.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiField, ApiTable } from "@/types/custom-field.types.ts";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { EmployeeCustomTableFieldEditModal } from "@/pages/settings/custom-fields/widgets/EmployeeCustomTableFieldEditModal.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";

interface EmployeeTableCustomFieldListProps {
  table: ApiTable;
}

export function EmployeeTableCustomFieldList({ table }: EmployeeTableCustomFieldListProps) {
  const { data: fields } = useTableFields(table.id);
  const { mutateAsync: fieldsSort } = useTableFieldsSort();
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { isFeatureEnabled } = usePermissions();
  const canCreateCustomFields = isFeatureEnabled(ApiFeatureType.custom_fields);

  const [optimisticFields, setOptimisticFields] = useState<{
    source: ApiField[] | undefined;
    items: ApiField[];
  } | null>(null);

  const currentFields =
    (optimisticFields?.source === fields ? optimisticFields?.items : fields) ?? [];

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.customFields.sortingViewed);
  });

  const computedData: CustomDraggableNode<ApiField>[] = useMemo(() => {
    if (!currentFields) return [];
    return currentFields.map((field) => ({
      id: field.id,
      type: CustomDraggableNodeType.field,
      data: field,
    }));
  }, [currentFields]);

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;

      const updatedFields = [...currentFields];
      const [movedField] = updatedFields.splice(result.source.index, 1);
      updatedFields.splice(result.destination.index, 0, movedField);

      setOptimisticFields({ source: fields, items: updatedFields });

      try {
        await fieldsSort({
          tableId: table.id,
          payload: {
            field_ids: updatedFields.map((f) => f.id),
          },
        });
      } catch {
        setOptimisticFields(null);
      }
    },
    [currentFields, fields, table, fieldsSort]
  );

  if (!currentFields) {
    return (
      <Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
      </Stack>
    );
  }

  const handleCreateField = () => {
    openModal(EmployeeCustomTableFieldEditModal, { table: table });
  };

  return (
    <Stack>
      <Title
        header={t("custom_fields:fields")}
        caption={t("custom_fields:create_fields_for_table")}
        paddingTop
        paddingBottom
        size="lg"
      />
      <Stack gap={"md"}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <HighlightDroppable droppableId={`fields-${table.id}`} type="field">
            {(provided) => (
              <Stack {...provided.droppableProps} ref={provided.innerRef}>
                {computedData.map((field, index) => (
                  <CustomDraggableItem
                    key={`field-${field.id}`}
                    node={field}
                    index={index}
                    table={table}
                  />
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </HighlightDroppable>
        </DragDropContext>
        {canCreateCustomFields && (
          <DropdownMenu
            trigger={
              <Button
                className={"w-full"}
                size={"lg"}
                variant={"secondary"}
                leftIcon={<FontAwesomeIcon icon="fa-solid fa-plus" />}
              >
                {t("common:create")}...
              </Button>
            }
          >
            <DropdownMenuItem onClick={handleCreateField} title={t("custom_fields:new_field")} />
          </DropdownMenu>
        )}
      </Stack>
    </Stack>
  );
}
